import React, { useEffect, useMemo, useRef, useState } from "react";
import { updateAssociationWorkingHours } from "../../services/associationService";
import { AssociationResponse } from "../../types/association.types";
import { WorkingHoursEntry } from "../../types/shared";
import { shouldRefetchAssociationsAfterError } from "../../utils/associationErrors";
import {
  HOURS_SAVED_NOTICE,
  isNotAdministeredError,
  mapWorkingHoursError,
  toAssociationEntries,
  toEditorEntries,
} from "../../utils/associationHours";
import WorkingHoursEditor, {
  HoursDraft,
  buildHoursDraft,
  hoursDraftToEntries,
} from "../VolunteerProfilePage/WorkingHoursEditor";
import styles from "./HoursCard.module.scss";

// Association portal, slice 3: the opening hours card on `/association`.
//
// The shared WorkingHoursEditor brings the day rows, its strict `from < until`
// validation and its clear-to-empty confirmation; this component owns the
// card, the draft, the save and the association-specific copy. The editor's
// own "saved" notice speaks of availability, so it is never raised here; the
// card shows its own, inline, like the other cards on the page.
//
// Seeding: the draft is seeded from `association.workingHours` on mount, when
// the selected association CHANGES, and after this card's own successful
// save. Any other arrival of the association (another card's save re-reading
// `/mine`) leaves the draft alone, so it never wipes what is being typed.
//
// The page's cross-card lock: the card reports its save in flight
// (`onPendingChange`) and whether the draft differs from the stored schedule
// (`onDirtyChange`, for the confirm before an association switch), both false
// on unmount, and holds its own save while `locked` (another card saves).

export const HOURS_CARD_TITLE = "Radno vreme udruženja";
export const HOURS_SAVE_LABEL = "Sačuvaj radno vreme";
export const HOURS_HINT =
  "Dan bez unetog vremena znači da je udruženje tog dana zatvoreno.";
export const HOURS_CLEAR_TITLE = "Uklanjate radno vreme";
export const HOURS_CLEAR_BODY = "Udruženje neće imati objavljeno radno vreme.";
export const HOURS_CLEAR_LABEL = "Ukloni radno vreme";

interface HoursCardProps {
  association: AssociationResponse;
  /** The parent re-reads `GET /association/mine`. */
  onSaved: () => void;
  /**
   * Optional: the page-level ASSOCIATION_NOT_ADMINISTERED handler (copy that
   * outlives the card, plus the re-read). Without it the card shows the copy
   * itself and asks for the re-read through `onSaved`.
   */
  onNotAdministered?: () => void;
  /** This card's save is in flight (the page's cross-card lock). */
  onPendingChange?: (pending: boolean) => void;
  /** The draft differs from the stored schedule. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Another card's save is in flight: this card's save waits for it. */
  locked?: boolean;
}

const draftFrom = (association: AssociationResponse): HoursDraft =>
  buildHoursDraft(toEditorEntries(association.workingHours));

/** What a save would send: equal keys mean nothing to save. */
const scheduleKey = (draft: HoursDraft): string =>
  JSON.stringify(hoursDraftToEntries(draft));

const HoursCard: React.FC<HoursCardProps> = ({
  association,
  onSaved,
  onNotAdministered,
  onPendingChange,
  onDirtyChange,
  locked = false,
}) => {
  const [seededFor, setSeededFor] = useState(association.id);
  const [draft, setDraft] = useState<HoursDraft>(() => draftFrom(association));
  // The stored schedule the draft was last seeded from.
  const [baseline, setBaseline] = useState<HoursDraft>(draft);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // A different association: re-seed during render, so the new association's
  // card never paints the previous one's draft, not even for one frame. A
  // save still in flight keeps `saving` (and the page lock): only that
  // request's completion releases it.
  if (seededFor !== association.id) {
    const fresh = draftFrom(association);
    setSeededFor(association.id);
    setDraft(fresh);
    setBaseline(fresh);
    setSaveError(null);
    setSaved(false);
  }

  const dirty = useMemo(
    () => scheduleKey(draft) !== scheduleKey(baseline),
    [draft, baseline],
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onPendingChange?.(saving);
  }, [saving, onPendingChange]);
  // An unmounted card holds no edits and no save the page must wait for.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  // Bumped when the association changes and on unmount: a save still in
  // flight for the previous association must not write its result (draft,
  // notice, error, lock) into this one.
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [association.id],
  );

  // Saving an empty list is allowed and clears the schedule. The shared
  // editor asks for an explicit confirmation before it ever calls `onSave([])`.
  const submit = async (entries: WorkingHoursEntry[]) => {
    if (saving || locked) {
      return;
    }
    const ticket = generation.current;
    const isCurrent = () => ticket === generation.current;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await updateAssociationWorkingHours(
        association.id,
        toAssociationEntries(entries),
      );
    } catch (error) {
      const notAdministered = isNotAdministeredError(error);
      if (isCurrent()) {
        // The draft stays as typed so the user can correct it. With a page
        // handler the ownership copy is shown once, at page level.
        setSaveError(
          notAdministered && onNotAdministered
            ? null
            : mapWorkingHoursError(error),
        );
      }
      // The lock belongs to the request, whichever association is shown now.
      setSaving(false);
      if (notAdministered && onNotAdministered) {
        onNotAdministered();
      } else if (shouldRefetchAssociationsAfterError(error)) {
        // Plan 1.3: the pairing is gone, or the association itself is (a 404
        // after another administrator deleted it): re-read `/mine`.
        onSaved();
      }
      return;
    }
    if (isCurrent()) {
      // The PUT replaces the schedule with exactly these rows and answers no
      // body, so they ARE the stored schedule: rebase the draft on them.
      const stored = buildHoursDraft(entries);
      setDraft(stored);
      setBaseline(stored);
      setSaved(true);
    }
    setSaving(false);
    onSaved();
  };

  const change = (next: HoursDraft) => {
    setDraft(next);
    setSaved(false);
  };

  return (
    <div className={styles.card} data-testid="hours-card">
      <WorkingHoursEditor
        draft={draft}
        onChange={change}
        onSave={submit}
        saving={saving}
        saveError={saveError}
        savedNotice={false}
        title={HOURS_CARD_TITLE}
        locked={locked}
        saveLabel={HOURS_SAVE_LABEL}
        hint={HOURS_HINT}
        clearConfirmTitle={HOURS_CLEAR_TITLE}
        clearConfirmBody={HOURS_CLEAR_BODY}
        clearConfirmLabel={HOURS_CLEAR_LABEL}
      />
      {saved && !saveError && (
        <p className={styles.notice} role="status">
          {HOURS_SAVED_NOTICE}
        </p>
      )}
    </div>
  );
};

export default HoursCard;
